import React, { useEffect, useState, useRef } from "react";
import { Breadcrumb } from "react-bootstrap";
import CustomTable from "../../components/DataTable/CustomTable";
import { Link, TableBody, TableCell, TableRow } from "@material-ui/core";
import PopupModal from "../../components/PopupModal.js";
import Alert from "../../components/Alert.js";
import {
  insertLeavePlan,
  deleteLeavePlan,
  getMyLeavePlans,
  updateLeavePlan,
  getYearsPlans,
} from "../../services/util";
import Popup from "../../components/Popup";
import UpdateLeavePlan from "./UpdateLeavePlan";

const headCells = [
  { id: "strFirstName", title: "Employee Name", disableSorting: false },
  { id: "dtStartDate", title: "Start Date", disableSorting: true },
  { id: "dtEndDate", title: "End Date", disableSorting: true },
  { id: "strReason", title: "Reason", disableSorting: true },
  { id: "action", title: "Action", disableSorting: true },
];

let empData = JSON.parse(sessionStorage.getItem("getUser"));

export default function LeavePlans() {
  const [records, setRecords] = useState([]);
  const [editLeaveRecord, setLeaveRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openPopup, setOpenPopup] = useState(false);
  const [yearsInDb, setYearsInDb] = useState([]);
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear().toString());
  

  // Alert states
  const [openNotification, setOpenNotification] = useState(false);
  const [alertProps, setAlertProps] = useState({
    title: "",
    message: "",
    textBox: false,
    alertOk: () => setOpenNotification(false),
    cancel: false,
    handleClose: () => setOpenNotification(false),
  });

  // Confirmation for Delete
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Ref for first effect run
  const firstFlag = useRef(true);

  const triggerPopup = (message, onOk = () => {}, showCancel = false) => {
    setAlertProps({
      title: "",
      message: message,
      textBox: false,
      alertOk: onOk,
      cancel: showCancel,
      handleClose: () => setOpenNotification(false),
    });
    setOpenNotification(true);
  };

  // Initial fetch
  useEffect(() => {
    if (firstFlag.current) {
      getYearsPlans()
        .then((response) => setYearsInDb(response.data))
        .catch((error) => {
          if (error.message === "Request failed with status code 401") {
            window.dispatchEvent(new Event("sessionExpired"));
          } else {
            triggerPopup(error.message);
          }
        });
      firstFlag.current = false;
    }
    getMyLeavePlans(empData.strEmpID, selectedYear)
      .then((response) => {
        setRecords(response.data);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.message === "Request failed with status code 401") {
          window.dispatchEvent(new Event("sessionExpired"));
        } else {
          triggerPopup(error.message);
        }
      });
  }, [selectedYear]);

  const [filterFn] = useState({ fn: (items) => items });
  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } = CustomTable(records, headCells, filterFn);

  const addLeavePlan = async (value) => {
    setIsLoading(true);
    await insertLeavePlan(value)
      .then((response) => {
        if (response.data[0].Result === "Success") {
          triggerPopup(response.data[0].Message);
          refetchData();
          setLeaveRecord(null);
          setOpenPopup(false);
        } else {
          triggerPopup(response.data[0].Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.message === "Request failed with status code 401") {
          window.dispatchEvent(new Event("sessionExpired"));
        } else {
          triggerPopup(error.message);
        }
      });
  };

  const refetchData = () => {
    getMyLeavePlans(empData.strEmpID, selectedYear)
      .then((response) => setRecords(response.data))
      .catch((error) => {
        setIsLoading(false);
        if (error.message === "Request failed with status code 401") {
          window.dispatchEvent(new Event("sessionExpired"));
        } else {
          triggerPopup(error.message);
        }
      });
  };

  const saveLeavePlan = async (value) => {
    setIsLoading(true);
    await updateLeavePlan(value)
      .then((response) => {
        if (response.data[0].Result === "Success") {
          triggerPopup(response.data[0].Message);
          setLeaveRecord(null);
          setOpenPopup(false);
          refetchData();
        } else {
          triggerPopup(response.data[0].Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.message === "Request failed with status code 401") {
          window.dispatchEvent(new Event("sessionExpired"));
        } else {
          triggerPopup(error.message);
        }
      });
  };

  // Show confirmation dialog before delete
  const handleDeleteClick = (id) => {
    setAlertProps({
      title: "Are you sure?",
      message: "Do you want to delete this?",
      textBox: false,
      alertOk: () => {
        deleteLeaveConfirmed(id); 
        setOpenNotification(false);
      },
      cancel: true, 
      handleClose: () => setOpenNotification(false),
    });
    setIsLoading(false);
    setOpenNotification(true);
  };


  // Actual delete logic
  const deleteLeaveConfirmed = async (id) => {
    let data = { empId: empData.strEmpID, idReq: id };
    setIsLoading(true);
    deleteLeavePlan(data)
      .then(() => {
        triggerPopup("Record deleted successfully!");
        refetchData();
      })
      .catch((error) => {
        if (error.message === "Request failed with status code 401") {
          window.dispatchEvent(new Event("sessionExpired"));
        } else {
          triggerPopup(error.message);
        }
      });
    setIsLoading(false);
    setPendingDeleteId(null);
  };

  const cancelLeavePlan = () => {
    setLeaveRecord(null);
    setOpenPopup(false);
  };

  const DateConvert = (inputDate) => {
    let date = new Date(inputDate);
    return new Intl.DateTimeFormat("en-IN").format(date);
  };

  const handleYearChange = (event) => {
    setIsLoading(true);
    setSelectedYear(event.target.value);
  };

  return (
    <>
      {openNotification && (
        <Alert
          openPopup={openNotification}
          title={alertProps.title}
          message={alertProps.message}
          textBox={alertProps.textBox}
          alertOk={alertProps.alertOk}
          cancel={alertProps.cancel}
          handleClose={alertProps.handleClose}
        />
      )}
      <div className="container-fluid">
        <div className={isLoading ? "overlay" : "hideLoading"}>
          <div className="loader">Loading...</div>
        </div>
        <Breadcrumb>
          <Breadcrumb.Item href="#">Home</Breadcrumb.Item>
          <Breadcrumb.Item active>Leave Management</Breadcrumb.Item>
          <Breadcrumb.Item active>My Leave Plans</Breadcrumb.Item>
        </Breadcrumb>
        <div className="card">
          <div className="card-header bg-primary text-white">My Leave Plans</div>
          <div className="card-body">
            <div className="row">
              <label htmlFor="year" className="ml-4 p-1 text-muted">Year:</label>
              <div className="form-group">
                <select
                  className="form-control ml-1"
                  value={selectedYear}
                  onChange={handleYearChange}
                  id="year"
                >
                  {yearsInDb.map((item) => (
                    <option key={item.Year} value={item.Year}>
                      {item.Year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6" style={{ paddingLeft: "0px" }}>
                {records.length < 1 && (
                  <span className="noRecord">No records found...</span>
                )}
              </div>
              <div className="form-group ml-auto mr-3">
                <button
                  type="button"
                  className="btn btn-primary font-weight-bold"
                  onClick={() => setOpenPopup(true)}
                >
                  Add Leave Plan
                </button>
              </div>
            </div>
            <TblContainer>
              <TblHead />
              <TableBody>
                {recordsAfterPagingAndSorting().map((item) => (
                  <TableRow key={item.idLeavePlan}>
                    <TableCell style={{ textAlign: "center" }}>
                      {item.strFirstName}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {DateConvert(item.dtStartDate)}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {DateConvert(item.dtEndDate)}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {item.strReason}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      <Link
                        onClick={() => {
                          setLeaveRecord(item);
                          setOpenPopup(true);
                        }}
                        href="#"
                      >
                        Edit
                      </Link>
                      <span> / </span>
                      <Link
                        onClick={() => handleDeleteClick(item.idLeavePlan)}
                        href="#"
                      >
                        Delete
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TblContainer>
            <TblPagination />
            <Popup
              openPopup={openPopup}
              title={"Leave Plan"}
              setOpenPopup={setOpenPopup}
              width={"sm"}
              setTopPadding={false}
            >
              <UpdateLeavePlan
                employeeId={empData.strEmpID}
                cancelLeavePlan={cancelLeavePlan}
                editLeaveRecord={editLeaveRecord}
                addLeavePlan={addLeavePlan}
                saveLeavePlan={saveLeavePlan}
              />
            </Popup>
          </div>
        </div>
      </div>
    </>
  );
}
